<script setup lang="ts">
import {
    ChevronLeft,
    Download,
    FileText,
    Hash,
    Maximize2,
    Megaphone,
    Minimize2,
    PanelRight,
    Printer,
} from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useSentryFeedback } from '@/composables/useSentryFeedback';

const { openFeedbackForm } = useSentryFeedback();

const props = defineProps<{
    projectName: string;
    saving: boolean;
    showRight: boolean;
    focusMode: boolean;
    numberingEnabled: boolean;
}>();

const title = defineModel<string>('title', { required: true });

const emit = defineEmits<{
    back: [];
    'toggle-right': [];
    'toggle-focus': [];
    'toggle-numbering': [];
    'export-pdf': [];
    'export-word': [];
}>();
</script>

<template>
    <header
        class="flex shrink-0 items-center justify-between gap-4 border-b border-border/40 bg-background px-4 py-2.5"
    >
        <!-- Gauche : retour + titre -->
        <div class="flex min-w-0 items-center gap-2">
            <Button
                variant="ghost"
                size="icon"
                class="h-8 w-8 shrink-0"
                :title="`Retour au projet ${props.projectName}`"
                @click="emit('back')"
            >
                <ChevronLeft class="h-4 w-4" />
            </Button>

            <div class="min-w-0">
                <p
                    class="truncate text-[10px] font-medium text-muted-foreground"
                >
                    {{ props.projectName }}
                </p>
                <Input
                    v-model="title"
                    placeholder="Titre du document"
                    class="h-6 truncate border-none bg-transparent p-0 text-sm font-semibold text-foreground shadow-none focus-visible:ring-0"
                />
            </div>
        </div>

        <!-- Droite : état + toggles -->
        <div class="flex shrink-0 items-center gap-3">
            <span
                class="flex items-center gap-1.5 text-xs text-muted-foreground"
            >
                <span
                    :class="[
                        'inline-block h-1.5 w-1.5 rounded-full',
                        props.saving
                            ? 'animate-pulse bg-amber-500'
                            : 'bg-emerald-500',
                    ]"
                />
                {{ props.saving ? 'Enregistrement…' : 'Enregistré' }}
            </span>

            <span
                v-if="props.focusMode"
                class="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
            >
                Mode concentré
            </span>

            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        class="h-7 gap-1.5 text-xs"
                        title="Exporter le document"
                    >
                        <Download class="h-3.5 w-3.5" />
                        <span>Exporter</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem @click="emit('export-pdf')">
                        <Printer class="h-4 w-4" />
                        <span>Exporter en PDF</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem @click="emit('export-word')">
                        <FileText class="h-4 w-4" />
                        <span>Exporter en Word (.docx)</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Button
                variant="outline"
                size="sm"
                class="h-7 gap-1.5 border-orange-300 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950"
                title="Signaler un problème ou proposer une amélioration"
                @click="openFeedbackForm"
            >
                <Megaphone class="h-3.5 w-3.5" />
                <span>Signaler · Suggérer</span>
            </Button>

            <div class="h-4 w-px bg-border/60" />

            <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7"
                :class="{
                    'bg-accent text-accent-foreground': props.numberingEnabled,
                }"
                :title="
                    props.numberingEnabled
                        ? 'Désactiver la numérotation des sections'
                        : 'Numéroter les sections automatiquement'
                "
                @click="emit('toggle-numbering')"
            >
                <Hash class="h-4 w-4" />
            </Button>

            <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7"
                :class="{ 'bg-accent text-accent-foreground': props.focusMode }"
                :title="
                    props.focusMode
                        ? 'Quitter le mode concentré'
                        : 'Mode concentré'
                "
                @click="emit('toggle-focus')"
            >
                <Minimize2 v-if="props.focusMode" class="h-4 w-4" />
                <Maximize2 v-else class="h-4 w-4" />
            </Button>

            <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7"
                :class="{ 'bg-accent text-accent-foreground': props.showRight }"
                title="Afficher / masquer le panneau droit"
                @click="emit('toggle-right')"
            >
                <PanelRight class="h-4 w-4" />
            </Button>
        </div>
    </header>
</template>
